import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { OrderStateService } from '../../../order/services/order-state.service';
import { TouchKeyboardService } from '../../services/touch-keyboard.service';
import { VuCommunicationService } from '../../services/vu-communication.service';
import { SmartCardCommunicationService } from '../../../smart-card/services/smart-card-communication.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';
import { SmartCardStatus } from '../../../smart-card/models/smart-card-status';
import { StatusConnectionEnum } from '../../../smart-card/models/status-connection-enum';
import { TouchTileService } from '../../services/touch-tile.service';

@Component({
  selector: 'app-vu-payment-complete',
  templateUrl: './vu-payment-complete.component.html',
  styleUrls: ['./vu-payment-complete.component.css']
})
export class VuPaymentCompleteComponent implements OnInit, OnDestroy {

  private releaseCardSubscription: Subscription;
  showPleaseTakeYouCard: boolean;
  private ledSubscription: Subscription;

  constructor(
    private router: Router,
    private shopSettingsService: ShopSettingsService,
    private vuCommunicationService: VuCommunicationService,
    private touchKeyboardService: TouchKeyboardService,
    @Inject(DOCUMENT) private document: Document,
    private orderStateService: OrderStateService,
    private smartCardCommunicationService: SmartCardCommunicationService,
    private loadingSpinnerService: LoadingSpinnerService,
    private touchTileService: TouchTileService,
  ) {
  }

  ngOnInit(): void {
    this.loadingSpinnerService.show();
    this.showPleaseTakeYouCard = false;
    this.shopSettingsService.isVUMode = true;
    this.document.body.classList.add('no-select');
    this.document.body.classList.add('no-drag');
    this.vuCommunicationService.initialize();
    this.touchKeyboardService.hide();
    this.ledSubscription = this.smartCardCommunicationService.ledEnabled.subscribe(state => {
      this.touchTileService.cardDispenserEnable(state);
    });
    this.releaseCardSubscription = this.canReleaseCard
      .pipe(
        switchMap(
          canReleaseCard => {
            if (canReleaseCard) {
              return this.releaseCard();
            } else {
              return of(true);
            }
          }),
      ).subscribe(
        _ => {
          this.navigateToMainPage();
        },
        (error) => {
          this.navigateToMainPage();
        }
      );
  }

  ledUnsubscribe(): void {
    if (this.ledSubscription) {
      this.ledSubscription.unsubscribe();
      this.ledSubscription = null;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeReleaseCardSubscription();
    this.loadingSpinnerService.hide();
    this.touchTileService.cardDispenserEnable(false);
    this.ledUnsubscribe();
  }

  private releaseCard(): Observable<any> {
    return this.smartCardCommunicationService.getStatus()
      .pipe(
        switchMap(status => {
          if (!status.isCardInserted) {
            return of(false);
          }
          const waitCardTakenSec = 30;
          this.showPleaseTakeYouCard = true;
          return this.smartCardCommunicationService.releaseCard(waitCardTakenSec);
        }),
        tap(_ => { this.showPleaseTakeYouCard = false; }),
      );
  }

  private unsubscribeReleaseCardSubscription(): void {
    if (this.releaseCardSubscription) {
      this.releaseCardSubscription.unsubscribe();
      this.releaseCardSubscription = null;
    }
  }

  get canReleaseCard(): Observable<boolean> {
    return this.guestCardInOrder.pipe(
      switchMap(result => {
        if (!result) {
          return of(result);
        }

        return this.cardAvailableForRelease;
      })
    );
  }

  get cardAvailableForRelease(): Observable<boolean> {
    return of(this.smartCardCommunicationService.enabled).pipe(
      switchMap(enabled => {
        if (!enabled) {
          return of(null);
        }
        return this.smartCardCommunicationService.getStatus();
      }),
      map((status: SmartCardStatus) => status && status.status === StatusConnectionEnum.Connected && status.isCardInserted),
    );
  }

  get guestCardInOrder(): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        if (!this.shopSettingsService.shopSettings.guestCardProduct) {
          return of(false);
        }
        return this.loadOrder();
      }),
      map(result => {
        if (!result
          || !this.orderStateService.order
          || !this.orderStateService.order.isPaid
          || !this.orderStateService.order.orderLines
          || this.orderStateService.order.totalAmount < 0
        ) {
          return false;
        }
        return this.orderStateService.order.orderLines
          .some(orderLine => orderLine.productId === this.shopSettingsService.shopSettings.guestCardProduct);
      })
    );
  }

  loadOrder(): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        const orderId = this.orderStateService.sessionOrderId;
        if (orderId === 0) {
          return of(false);
        }
        if (this.orderStateService.order && this.orderStateService.order.id === orderId) {
          return of(true);
        }
        return this.orderStateService.loadOrder(orderId);
      }));
  }

  onClickContinueShopping(): void {
    this.navigateToMainPage();
  }

  private navigateToMainPage(): void {
    if (this.shopSettingsService.profile) {
      this.router.navigateByUrl('/vu?profile=' + this.shopSettingsService.profile);
    } else {
      this.router.navigateByUrl('/vu');
    }
  }
}
